import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Spinner, Card, Modal, Input, Textarea, Select, PrimaryButton, GhostButton } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import {
  User, School, Building2, Dumbbell, Flag, Map, Users, Rocket, Plus, Search, MapPin, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = { athletes: User, schools: School, colleges: Building2, universities: Building2, academies: Dumbbell, facilities: Dumbbell, events: Flag, tourism: Map, communities: Users, startups: Rocket };

export default function Discover() {
  const [cats, setCats] = useState(null);
  const [active, setActive] = useState("athletes");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null); // 'athlete' | 'community' | 'startup'

  const load = () => api.get("/discover").then((r) => setCats(r.data.categories)).catch(() => setCats([]));
  useEffect(() => { load(); }, []);
  if (!cats) return <Spinner />;

  const current = cats.find((c) => c.key === active) || cats[0];
  const items = (current?.items || []).filter((i) => {
    if (!q) return true;
    const hay = JSON.stringify(i).toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const createAction = { athletes: "athlete", communities: "community", startups: "startup" }[active];

  return (
    <div className="animate-fadeUp">
      <PageHeader title="Discover" subtitle="Explore the SPORA1 sports ecosystem" />

      <div className="mb-5 flex flex-wrap gap-2">
        {cats.map((c) => {
          const Icon = ICONS[c.key] || Compass;
          const on = c.key === active;
          return (
            <button key={c.key} data-testid={`discover-cat-${c.key}`} onClick={() => setActive(c.key)}
              className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all ${
                on ? "border-primary bg-primary/15 text-primary" : "border-border bg-card text-muted-foreground hover:border-primary/40"}`}>
              <Icon style={{ width: 15, height: 15 }} /> {c.label}
              <span className="rounded-full bg-background px-1.5 text-xs">{c.items.length}</span>
            </button>
          );
        })}
      </div>

      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <input data-testid="discover-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${current?.label}…`}
            className="w-full rounded-lg border border-input bg-background py-2.5 pl-10 pr-3 text-sm outline-none focus:border-primary" />
        </div>
        {createAction && (
          <PrimaryButton data-testid="discover-add-btn" onClick={() => setModal(createAction)}>
            <Plus className="h-4 w-4" /> Add
          </PrimaryButton>
        )}
      </div>

      {items.length === 0 ? (
        <Card className="text-center text-sm text-muted-foreground">No {current?.label} listed yet{createAction ? " - be the first to add one." : "."}</Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it, idx) => <DiscoverCard key={it.id || idx} cat={active} item={it} />)}
        </div>
      )}

      <CreateModal type={modal} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />
    </div>
  );
}

function isSafeUrl(u) {
  if (!u) return null;
  try {
    const url = new URL(u);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return url.href;
  } catch {
    return null;
  }
}

function DiscoverCard({ cat, item }) {
  const title = item.display_name || item.name;
  const sub = item.sport || item.category || item.type || item.level || "";
  const loc = [item.city, item.state].filter(Boolean).join(", ");
  const website = isSafeUrl(item.website);
  return (
    <div className="rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-primary/50">
      <div className="flex items-start justify-between">
        <h3 className="font-display text-lg font-semibold">{title}</h3>
        <div className="flex flex-col items-end gap-1">
          {(item.status || item.level) && <StatusBadge status={item.status || item.level} />}
          {cat === "athletes" && item.open_to_join && (
            <span data-testid={`open-to-play-${item.id}`} className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">Open to play</span>
          )}
        </div>
      </div>
      {sub && <p className="mt-1 text-sm capitalize text-primary">{sub}</p>}
      {(item.description || item.bio) && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{item.description || item.bio}</p>}
      <div className="mt-3 flex items-center justify-between gap-2">
        {loc && <p className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {loc}</p>}
        {website && (
          <a href={website} target="_blank" rel="noopener noreferrer" data-testid={`visit-website-${item.id}`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/20">
            Visit Website <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  );
}

function CreateModal({ type, onClose, onDone }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  if (!type) return null;

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (type === "athlete") await api.post("/athletes", { display_name: form.display_name, sport: form.sport, city: form.city, bio: form.bio, level: form.level || "amateur" });
      if (type === "community") await api.post("/communities", { name: form.name, sport: form.sport, city: form.city, description: form.description });
      if (type === "startup") await api.post("/startups", { name: form.name, category: form.category, city: form.city, website: form.website, description: form.description });
      toast.success("Added to Discover"); setForm({}); onDone();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const titles = { athlete: "Create athlete profile", community: "Create community", startup: "List a sports startup" };

  return (
    <Modal open={!!type} onClose={onClose} title={titles[type]} wide>
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
        {type === "athlete" && <>
          <Input label="Display name" testId="d-athlete-name" required value={form.display_name || ""} onChange={set("display_name")} />
          <Input label="Sport" testId="d-athlete-sport" required value={form.sport || ""} onChange={set("sport")} />
          <Input label="City" testId="d-athlete-city" value={form.city || ""} onChange={set("city")} />
          <Select label="Level" testId="d-athlete-level" value={form.level || "amateur"} onChange={set("level")}>
            <option value="amateur">Amateur</option><option value="school">School</option><option value="state">State</option><option value="national">National</option>
          </Select>
          <Textarea label="Bio" testId="d-athlete-bio" rows={2} value={form.bio || ""} onChange={set("bio")} className="sm:col-span-2" />
        </>}
        {type === "community" && <>
          <Input label="Community name" testId="d-comm-name" required value={form.name || ""} onChange={set("name")} className="sm:col-span-2" />
          <Input label="Sport" testId="d-comm-sport" value={form.sport || ""} onChange={set("sport")} />
          <Input label="City" testId="d-comm-city" value={form.city || ""} onChange={set("city")} />
          <Textarea label="Description" testId="d-comm-desc" rows={2} value={form.description || ""} onChange={set("description")} className="sm:col-span-2" />
        </>}
        {type === "startup" && <>
          <Input label="Startup name" testId="d-startup-name" required value={form.name || ""} onChange={set("name")} />
          <Input label="Category" testId="d-startup-category" value={form.category || ""} onChange={set("category")} placeholder="e.g. Analytics, Gear" />
          <Input label="City" testId="d-startup-city" value={form.city || ""} onChange={set("city")} />
          <Input label="Website" testId="d-startup-website" value={form.website || ""} onChange={set("website")} />
          <Textarea label="Description" testId="d-startup-desc" rows={2} value={form.description || ""} onChange={set("description")} className="sm:col-span-2" />
        </>}
        <div className="flex justify-end gap-2 sm:col-span-2">
          <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton data-testid="d-create-submit" type="submit" disabled={saving}>Add</PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}

const Compass = Search;
